import type { Floor, ProjectState, Roof } from '../types';

// ── Niveau Toiture (comme Revit) ───────────────────────────
// Les toits sont dessines et edites sur un niveau dedie « Toiture », toujours au-dessus des etages.
// Chaque toit s'appuie sur un etage (supportFloorId) dont les murs exterieurs donnent son contour.

export const isRoofLevel = (f: Pick<Floor, 'kind'> | undefined): boolean => f?.kind === 'toiture';

/** Etages courants (hors niveau Toiture) */
export const regularFloors = (floors: Floor[]): Floor[] => floors.filter(f => !isRoofLevel(f));

/** Etage dont les murs portent le toit */
export const roofSupportFloorId = (roof: Pick<Roof, 'floorId' | 'supportFloorId'>): string => roof.supportFloorId ?? roof.floorId;

/** Nom de l'etage d'appui d'un toit : celui dont les murs donnent son contour, pas le niveau ou il est dessine (FIX-032) */
export function roofSupportFloorName(roof: Pick<Roof, 'floorId' | 'supportFloorId'>, floors: Pick<Floor, 'id' | 'name'>[]): string {
  return floors.find(f => f.id === roofSupportFloorId(roof))?.name ?? '?';
}

/**
 * Altitude (m) ou repose un toit : le haut des murs de son etage d'appui, ou le haut du chainage (dalle haute) qui
 * les coiffe, s'il y en a un. @param elevation altitude (m) de l'etage ; @param wallTop hauteur (m) des murs ;
 * @param support l'etage d'appui, s'il porte un chainage (la 3D n'en construit que sous les murs exterieurs)
 */
export function roofBaseY(elevation: number, wallTop: number, support?: Pick<Floor, 'height' | 'slabThickness'>): number {
  const slab = support?.slabThickness ?? 0;
  const chainageTop = slab > 0 ? (support!.height + slab) / 100 : 0;
  return elevation + Math.max(wallTop, chainageTop);
}

/** Niveau Toiture du projet (le premier s'il y en a plusieurs) */
export const roofLevel = (floors: Floor[]): Floor | undefined => floors.find(isRoofLevel);

/** Etage le plus haut (hors Toiture) : appui par defaut d'un nouveau toit cree depuis le niveau Toiture */
export function topRegularFloor(floors: Floor[]): Floor | undefined {
  return [...regularFloors(floors)].sort((a, b) => b.level - a.level)[0];
}

export function newRoofLevel(id: string, floors: Floor[]): Floor {
  const top = topRegularFloor(floors);
  return { id, name: 'Toiture', level: (top?.level ?? 0) + 1, height: 0, kind: 'toiture' };
}

/**
 * Remet le projet en ordre : s'il y a des toits, un niveau Toiture existe, au-dessus de tous les etages,
 * et chaque toit y est rattache en gardant son etage d'appui. Renvoie le meme objet si rien ne change.
 */
export function ensureRoofLevel(p: ProjectState, makeId: () => string): ProjectState {
  const roofs = p.roofs || [];
  let floors = p.floors;
  let level = roofLevel(floors);
  if (!roofs.length && !level) return p;
  if (!level) {
    level = newRoofLevel(makeId(), floors);
    floors = [...floors, level];
  }
  // Toujours au-dessus des etages
  const top = topRegularFloor(floors);
  if (top && level.level <= top.level) {
    const lid = level.id;
    level = { ...level, level: top.level + 1 };
    floors = floors.map(f => (f.id === lid ? level! : f));
  }
  let changedRoofs = false;
  const nextRoofs = roofs.map(r => {
    if (r.floorId === level!.id) return r;
    changedRoofs = true;
    return { ...r, supportFloorId: roofSupportFloorId(r), floorId: level!.id };
  });
  if (floors === p.floors && !changedRoofs) return p;
  return { ...p, floors, roofs: changedRoofs ? nextRoofs : roofs };
}
